import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { StarRating } from './StarRating';
import { ImprovementSelector } from './ImprovementSelector';

const ModalOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const ModalContent = styled.div`
  ${tw`bg-white rounded-modal w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col`}
`;

const ModalHeader = styled.div`
  ${tw`flex justify-between items-center px-6 py-4 border-b border-border-default`}
`;

const HeaderLeft = styled.div`
  ${tw`flex items-center gap-3`}
`;

const BackButton = styled.button`
  ${tw`text-text-tertiary hover:text-text-secondary cursor-pointer`}
`;

const Title = styled.h2`
  ${tw`text-lg font-semibold`}
`;

const CloseButton = styled.button`
  ${tw`text-text-tertiary hover:text-text-secondary cursor-pointer`}
`;

const ModalBody = styled.div`
  ${tw`px-6 py-6 overflow-y-auto flex-1`}
`;

const HostInfo = styled.div`
  ${tw`flex items-center gap-3 mb-6`}
`;

const Avatar = styled.div`
  ${tw`w-12 h-12 rounded-full bg-bg-secondary flex items-center justify-center text-text-secondary font-semibold text-lg overflow-hidden`}
`;

const HostDetails = styled.div`
  ${tw`flex flex-col`}
`;

const HostName = styled.span`
  ${tw`font-semibold text-text-primary`}
`;

const TripDetails = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const StepIndicator = styled.div`
  ${tw`flex justify-center gap-2 mb-6`}
`;

const StepDot = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
  ${tw`w-2 h-2 rounded-full transition-colors`}
  ${({ $isActive, $isCompleted }) =>
    $isActive ? tw`bg-black` : $isCompleted ? tw`bg-purple-300` : tw`bg-gray-300`}
`;

const CategoryTitle = styled.h3`
  ${tw`text-xl font-semibold text-center mb-2`}
`;

const CategoryDescription = styled.p`
  ${tw`text-sm text-text-secondary text-center mb-6`}
`;

const RatingSection = styled.div`
  ${tw`flex flex-col items-center py-4`}
`;

const CommentSection = styled.div`
  ${tw`mt-6`}
`;

const CommentLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-2`}
`;

const CommentTextarea = styled.textarea`
  ${tw`w-full px-4 py-3 border border-border-default rounded-lg resize-none focus:ring-2 focus:ring-black focus:border-transparent`}
  height: 120px;
`;

const ButtonContainer = styled.div`
  ${tw`px-6 py-4 border-t border-border-default flex gap-3`}
`;

const SkipButton = styled.button`
  ${tw`flex-1 py-3 px-4 bg-bg-secondary text-text-secondary font-semibold rounded-lg hover:bg-bg-secondary transition-colors cursor-pointer`}
`;

const NextButton = styled.button`
  ${tw`flex-1 py-3 px-4 bg-black text-white font-semibold rounded-lg hover:bg-black-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
`;

interface CategoryRating {
  score: number;
  improve?: string[];
}

interface GuestRatingData {
  general: CategoryRating;
  maintenance: CategoryRating;
  cleanliness: CategoryRating;
  communication: CategoryRating;
  convenience: CategoryRating;
  accuracy: CategoryRating;
  comment?: string;
}

interface GuestRatingHostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: GuestRatingData) => void;
  hostName: string;
  hostAvatar?: string;
  carName: string;
  tripDates: string;
  isSubmitting?: boolean;
}

const STEPS = [
  {
    key: 'general',
    title: 'Rate this trip',
    description: 'How was your overall experience?',
  },
  {
    key: 'maintenance',
    title: 'Maintenance',
    description: 'How well was the car maintained?',
  },
  {
    key: 'cleanliness',
    title: 'Cleanliness',
    description: 'How clean was the car?',
  },
  {
    key: 'communication',
    title: 'Communication',
    description: 'How was the communication with the host?',
  },
  {
    key: 'convenience',
    title: 'Convenience',
    description: 'How convenient was the pickup and drop-off?',
  },
  {
    key: 'accuracy',
    title: 'Accuracy',
    description: 'How accurate was the car listing?',
  },
];

export const GuestRatingHostModal = ({
  isOpen,
  onClose,
  onSubmit,
  hostName,
  hostAvatar,
  carName,
  tripDates,
  isSubmitting = false,
}: GuestRatingHostModalProps) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [ratings, setRatings] = useState<GuestRatingData>({
    general: { score: 0 },
    maintenance: { score: 0 },
    cleanliness: { score: 0 },
    communication: { score: 0 },
    convenience: { score: 0 },
    accuracy: { score: 0 },
  });
  const [comment, setComment] = useState<string>('');

  if (!isOpen) return null;

  const currentStepData = STEPS[currentStep];
  const categoryKey = currentStepData.key as keyof Omit<GuestRatingData, 'comment'>;
  const currentRating = ratings[categoryKey];
  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const updateRating = (score: number) => {
    setRatings(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        score,
        // Clear improvements if rating is 5
        improve: score === 5 ? undefined : prev[categoryKey].improve,
      },
    }));
  };

  const updateImprovements = (improvements: string[]) => {
    setRatings(prev => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        improve: improvements.length > 0 ? improvements : undefined,
      },
    }));
  };

  const handleSkip = () => {
    // Default to 5 stars
    setRatings(prev => ({
      ...prev,
      [categoryKey]: { score: 5 },
    }));

    if (isLastStep) {
      handleSubmit(5);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentRating.score === 0) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = (overrideLastScore?: number) => {
    const finalRatings = { ...ratings };
    if (overrideLastScore !== undefined) {
      finalRatings[categoryKey] = { score: overrideLastScore };
    }
    finalRatings.comment = comment.trim() || undefined;
    onSubmit(finalRatings);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const showImprovements =
    categoryKey !== 'general' && currentRating.score > 0 && currentRating.score <= 4;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <HeaderLeft>
            {!isFirstStep && (
              <BackButton onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </BackButton>
            )}
            <Title>{currentStepData.title}</Title>
          </HeaderLeft>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {isFirstStep && (
            <HostInfo>
              <Avatar>
                {hostAvatar ? (
                  <img
                    src={hostAvatar}
                    alt={hostName}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  getInitials(hostName)
                )}
              </Avatar>
              <HostDetails>
                <HostName>{hostName}</HostName>
                <TripDetails>
                  {carName} - {tripDates}
                </TripDetails>
              </HostDetails>
            </HostInfo>
          )}

          <StepIndicator>
            {STEPS.map((_, index) => (
              <StepDot
                key={index}
                $isActive={index === currentStep}
                $isCompleted={index < currentStep}
              />
            ))}
          </StepIndicator>

          {!isFirstStep && (
            <>
              <CategoryTitle>{currentStepData.title}</CategoryTitle>
              <CategoryDescription>{currentStepData.description}</CategoryDescription>
            </>
          )}

          {isFirstStep && <CategoryDescription>{currentStepData.description}</CategoryDescription>}

          <RatingSection>
            <StarRating rating={currentRating.score} onRatingChange={updateRating} showLabel />
          </RatingSection>

          {showImprovements && (
            <ImprovementSelector
              category={
                categoryKey as
                  | 'maintenance'
                  | 'cleanliness'
                  | 'communication'
                  | 'convenience'
                  | 'accuracy'
              }
              selectedImprovements={currentRating.improve || []}
              onImprovementsChange={updateImprovements}
            />
          )}

          {isFirstStep && (
            <CommentSection>
              <CommentLabel>Write a public review</CommentLabel>
              <CommentTextarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Share a public review to help inform the Autopilotee community."
              />
            </CommentSection>
          )}
        </ModalBody>

        <ButtonContainer>
          <SkipButton onClick={handleSkip}>Skip</SkipButton>
          <NextButton onClick={handleNext} disabled={currentRating.score === 0 || isSubmitting}>
            {isLastStep ? (isSubmitting ? 'Submitting...' : 'Submit') : 'Next'}
          </NextButton>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};
